import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setCurrentPage,
  setPageSize,
} from "../../../redux/suspensionPointSetting/suspensionPointSetting";
import { NavLink } from "react-router-dom";
import {
  SaveOutlined,
  CloseOutlined,
  PlusCircleOutlined,
  ShopOutlined,
} from "@ant-design/icons";
import {
  Breadcrumb,
  Button,
  Divider,
  Empty,
  Table,
  Form,
  message,
  Col,
  Row,
  InputNumber,
  Card,
  Space,
  Typography,
} from "antd";
import {
  deleteSuspensionPointSetting,
  getListSuspensionPointSetting,
  updateSuspensionPointSetting,
} from "../../../api/suspensionPointSetting/suspensionPointSetting";
import FormAddSuspensionPointSetting from "./FormAddSuspensionPointSetting";
import ButtonWithPermission from "../../../components/ButtonWithPermission/ButtonWithPermission";
import RowActions from "../../../components/cms/RowActions";
import { ACTION_ICON, confirmDelete } from "../../../components/cms/actions";
import { neutral } from "../../../theme/tokens";

export default function SuspensionPointSettingTable() {
  const {
    listSuspensionPointSetting,
    isLoadSuspensionPointSetting,
    currentPage,
    pageSize,
    totalRecords,
  } = useSelector(
    (state) => state.persistedReducer.suspensionPointSettingSlice
  );

  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [editingKey, setEditingKey] = useState("");
  const [editedRecord, setEditedRecord] = useState({});
  const { Text } = Typography;

  const showModal = () => {
    setIsModalOpen(true);
  };
  const handleOk = () => {
    setIsModalOpen(false);
  };
  const handleCancel = useCallback(() => {
    setIsModalOpen(false);
  }, [setIsModalOpen]);

  useEffect(() => {
    dispatch(getListSuspensionPointSetting({ currentPage, pageSize }));
  }, [dispatch, currentPage, pageSize]);

  const handlePageChange = (page) => {
    dispatch(setCurrentPage(page));
  };

  const handleSizeChange = useCallback(
    (current, size) => {
      setPageSize(size);
    },
    [setPageSize]
  );

  // Kiểm tra xem dòng có đang được chỉnh sửa không
  const isEditing = (record) => record.id === editingKey;

  // Bắt đầu chỉnh sửa
  const edit = (record) => {
    form.setFieldsValue({ ...record });
    setEditingKey(record.id);
    setEditedRecord(record);
  };

  // Hủy chỉnh sửa
  const cancel = () => {
    setEditingKey("");
  };

  // Lưu dữ liệu đã chỉnh sửa
  const save = async (id) => {
    try {
      const row = await form.validateFields();
      const updatedData = { ...row };
      const response = await dispatch(
        updateSuspensionPointSetting({ id, data: updatedData })
      );
      if (updateSuspensionPointSetting.fulfilled.match(response)) {
        message.success("Cập nhât dữ liệu thành công!");
        await dispatch(
          getListSuspensionPointSetting({ currentPage, pageSize })
        );
      } else if (updateSuspensionPointSetting.rejected.match(response)) {
        if (response.payload.message) {
          message.error(response.payload.message);
        }
        if (response.payload.errors) {
          Object.keys(response.payload.errors).forEach((field) => {
            const errorMessages = response.payload.errors[field];
            errorMessages.forEach((errorMessage) => {
              message.error(`${errorMessage}`);
            });
          });
        }
      }
      setEditingKey("");
    } catch (err) {
      console.error("Có lỗi xảy ra:", err);
    }
  };

  const deleteEventRecord = async (id) => {
    try {
      await dispatch(deleteSuspensionPointSetting(id));
      dispatch(getListSuspensionPointSetting({ currentPage, pageSize }));
    } catch (err) {
      console.error("Error deleting service:", err);
    }
  };

  // Định nghĩa các cột cho bảng
  const columns = [
    {
      title: "Chi nhánh",
      dataIndex: "mcoupon_branch_id",
      key: "mcoupon_branch_id",
      fixed: 'left',
      width: 200,
      render: (_, record) => (
        <Space>
          <ShopOutlined />
          <Text>{record.mcoupon_branch?.name || "Không có dữ liệu"}</Text>
        </Space>
      ),
    },
    {
      title: "Thời gian (Ngày)",
      dataIndex: "date",
      key: "date",
      width: 200,
      render: (_, record) =>
        isEditing(record) ? (
          <Form.Item
            name="date"
            rules={[
              {
                required: true,
                message: "Vui lòng nhập số ngày kích hoạt điểm",
              },
              {
                type: "number",
                min: 0,
                message: "Số dư phải lớn hơn hoặc bằng 0",
              },
            ]}
            style={{ margin: 0 }}
          >
            <InputNumber
              placeholder="Nhập số ngày kích hoạt điểm"
              style={{ width: "100%" }}
              min={0}
              formatter={(value) =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
              parser={(value) => value.replace(/,/g, "")}
            />
          </Form.Item>
        ) : (
          record.date
        ),
    },
    {
      title: "Thao tác",
      dataIndex: "operation",
      align: "center",
      width: 200,
      fixed: 'right',
      render: (_, record) => {
        const editable = isEditing(record); // Kiểm tra nếu dòng đang chỉnh sửa
        return editable ? (
          <>
            <Button
              type="link"
              icon={<SaveOutlined />}
              onClick={() => save(record.id)} // Lưu dữ liệu
              style={{ marginRight: 8 }}
            >
              Lưu
            </Button>
            <Button
              type="link"
              icon={<CloseOutlined />}
              onClick={cancel} // Hủy chỉnh sửa
              danger
              style={{ color: neutral[500] }}
            >
              Hủy
            </Button>
          </>
        ) : (
          <RowActions
            disabled={editingKey !== ""}
            items={[
              {
                key: "edit",
                icon: ACTION_ICON.edit,
                label: "Chỉnh sửa",
                // Quyền chuyển vào `permission` — trước đây bọc ButtonWithPermission
                // bên trong Tooltip nên tooltip "Sửa" đè mất câu báo thiếu quyền.
                permission: ["suspension-point-settings.update"],
                onClick: () => edit(record),
              },
              {
                key: "delete",
                icon: ACTION_ICON.delete,
                label: "Xóa thiết lập",
                danger: true,
                divider: true,
                permission: ["suspension-point-settings.delete"],
                onClick: () =>
                  confirmDelete(`thiết lập #${record.id}`, () =>
                    deleteEventRecord(record.id)
                  ),
              },
            ]}
          />
        );
      },
    },
  ];

  return (
    <div>
      <div className="bg-gray-50 min-h-screen p-6">
        <Card className="mb-6 shadow-sm">
          <Breadcrumb separator=">">
            <Breadcrumb.Item>Trang chủ</Breadcrumb.Item>
            <Breadcrumb.Item>Cài đặt</Breadcrumb.Item>
            <Breadcrumb.Item>
              <NavLink to="/suspension-point-settings">
                Thiết lập điểm chưa khả dụng
              </NavLink>
            </Breadcrumb.Item>
          </Breadcrumb>

          <p style={{ fontSize: "12px", color: "#555" }}>
            Trang <strong>"Thiết lập điểm chưa khả dụng"</strong> cho phép quản
            trị viên cấu hình một khoảng thời gian chờ nhất định sau khi người
            dùng tích điểm, trước khi điểm đó trở thành khả dụng và có thể sử
            dụng trong hệ thống. Điều này giúp kiểm soát việc sử dụng điểm và
            đảm bảo rằng điểm tích lũy chỉ có thể sử dụng sau khi qua một thời
            gian nhất định, tránh các hành vi gian lận hoặc việc sử dụng điểm
            ngay lập tức.
          </p>
          <Divider />

          <Row gutter={[16, 16]}>
            <Col xl lg md sm>
              {/* <Button
                type="primary"
                onClick={showModal}
                icon={<PlusCircleOutlined />}
              >
                Thêm mới
              </Button> */}
              
              <ButtonWithPermission
                actions={["suspension-point-settings.store"]}
                children={"Thêm mới"}
                type="primary"
                icon={<PlusCircleOutlined />}
                onClick={(e) => {
                  e?.preventDefault();
                  showModal();
                }}
                noPermissionText={"Không có quyền truy cập"}
              />
              {isModalOpen && (
                <FormAddSuspensionPointSetting
                  isModalOpen={isModalOpen}
                  currentPage={currentPage}
                  pageSize={pageSize}
                  handleOk={handleOk}
                  handleCancel={handleCancel}
                />
              )}
            </Col>
          </Row>
          <Divider />

          <Form form={form} component={false}>
            <Table
              scroll={{ x: "max-content" }}
              columns={columns}
              loading={
                listSuspensionPointSetting?.length === 0
                  ? isLoadSuspensionPointSetting
                  : false
              }
              dataSource={
                isLoadSuspensionPointSetting ? [] : listSuspensionPointSetting
              } // Dữ liệu hiển thị trong bảng
              rowKey="id"
              pagination={{
                showTotal: (totalRecords, range) =>
                  `Hiển thị từ ${range[0]} -> ${range[1]} trong tổng ${totalRecords} giá trị`,
                showQuickJumper: true,
                showSizeChanger: true,
                onShowSizeChange: handleSizeChange,
                total: totalRecords,
                current: currentPage,
                onChange: handlePageChange,
                pageSizeOptions: [10, 20, 30],
              }}
              locale={{
                emptyText: <Empty description="Không có thông tin!" />,
              }}
              size="small"
            />
          </Form>
        </Card>
      </div>
    </div>
  );
}
